import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { DEFAULT_TEAMS, SEED_NODES, teamColor } from "./data.js";
import { ancestorIds, getDepths, rootIdsOf, subtreeIds } from "./layout.js";

const LS_KEY = "arbor-org-v3";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const DEFAULT_BG = { image: null, dim: 0.72, blur: 2 };

const makeAxios = () => {
  const instance = axios.create({
    baseURL: API_BASE,
    timeout: 15000,
  });
  instance.interceptors.request.use((config) => {
    try {
      const token = localStorage.getItem("token");
      if (token) config.headers.Authorization = `Bearer ${token}`;
      const csrf = localStorage.getItem("csrf_token");
      if (csrf && config.method && ["put", "post", "patch", "delete"].includes(config.method.toLowerCase())) {
        config.headers["X-CSRF-Token"] = csrf;
      }
    } catch (_) { /* ignore */ }
    return config;
  });
  return instance;
};

function loadPersisted() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed.nodes) || parsed.nodes.length === 0) return null;
    if (!parsed.nodes.some((n) => n.parentId === null)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function useOrg() {
  const [persisted] = useState(loadPersisted);
  const [nodes, setNodes] = useState(persisted?.nodes ?? SEED_NODES);
  const [mode, setMode] = useState(persisted?.mode ?? "vertical");
  const [cardSize, setCardSize] = useState(persisted?.cardSize ?? "M");
  const [teams, setTeams] = useState(persisted?.teams ?? DEFAULT_TEAMS);
  const [background, setBackground] = useState(
    persisted?.background ?? DEFAULT_BG
  );

  const serverLoadedRef = useRef(false);
  const saveTimerRef = useRef(null);

  // Hydrate from MongoDB (orgchart_state) on mount; server wins when present.
  useEffect(() => {
    let active = true;
    (async () => {
      const ax = makeAxios();
      try {
        const res = await ax.get('/orgchart/state');
        const s = res?.data?.state;
        if (
          active && s && Array.isArray(s.nodes) && s.nodes.length > 0 &&
          s.nodes.some((n) => n.parentId === null)
        ) {
          setNodes(s.nodes);
          if (s.mode) setMode(s.mode);
          if (s.cardSize) setCardSize(s.cardSize);
          if (s.teams) setTeams(s.teams);
          if (s.background) setBackground(s.background);
        }
      } catch {
        /* offline: keep local state */
      } finally {
        if (active) serverLoadedRef.current = true;
      }
    })();
    return () => { active = false; };
  }, []);

  const rootIds = useMemo(() => new Set(rootIdsOf(nodes)), [nodes]);

  const depths = useMemo(() => getDepths(nodes), [nodes]);
  const levels = useMemo(() => {
    let max = 0;
    depths.forEach((d) => { max = Math.max(max, d); });
    return max + 1;
  }, [depths]);
  const departmentNames = useMemo(
    () => Array.from(new Set(nodes.map((n) => n.department))),
    [nodes]
  );

  useEffect(() => {
    const payload = { nodes, mode, cardSize, teams, background };
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(payload));
    } catch {
      try {
        localStorage.setItem(
          LS_KEY,
          JSON.stringify({ ...payload, background: { ...background, image: null } })
        );
      } catch {
        /* storage unavailable */
      }
    }
    // Debounced persistence to MongoDB (only after the initial server load).
    if (serverLoadedRef.current) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        const ax = makeAxios();
        ax.put('/orgchart/state', { state: payload }).catch(() => {});
      }, 800);
    }
  }, [nodes, mode, cardSize, teams, background]);

  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  const addNode = useCallback(
    (parentId) => {
      const parent = nodes.find((n) => n.id === parentId);
      if (!parent) return null;
      const node = {
        id: `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
        name: "New Member",
        role: "New Role",
        department: parent.department,
        color: parent.color,
        parentId,
      };
      setNodes((prev) => [...prev, node]);
      return node;
    },
    [nodes]
  );

  const updateNode = useCallback(
    (id, patch) => {
      setNodes((prev) => {
        const full = { ...patch };
        if (patch.department) full.color = teamColor(teams, patch.department);
        return prev.map((n) => (n.id === id ? { ...n, ...full } : n));
      });
    },
    [teams]
  );

  const addTeam = useCallback((name, color) => {
    const clean = name.trim();
    if (!clean) return false;
    let ok = true;
    setTeams((prev) => {
      if (prev.some((t) => t.name.toLowerCase() === clean.toLowerCase())) {
        ok = false;
        return prev;
      }
      return [...prev, { name: clean, color }];
    });
    return ok;
  }, []);

  const removeTeam = useCallback((name) => {
    let moved = 0;
    setTeams((prevTeams) => {
      if (prevTeams.length <= 1) return prevTeams;
      const next = prevTeams.filter((t) => t.name !== name);
      const fallback = next[0];
      setNodes((prevNodes) =>
        prevNodes.map((n) => {
          if (n.department !== name) return n;
          moved++;
          return { ...n, department: fallback.name, color: fallback.color };
        })
      );
      return next;
    });
    return moved;
  }, []);

  const updateTeam = useCallback((name, patch) => {
    setTeams((prev) =>
      prev.map((t) => (t.name === name ? { ...t, ...patch } : t))
    );
    setNodes((prev) =>
      prev.map((n) =>
        n.department === name
          ? {
              ...n,
              department: patch.name ?? n.department,
              color: patch.color ?? n.color,
            }
          : n
      )
    );
  }, []);

  const addRootNode = useCallback(() => {
    const team = teams[0];
    const node = {
      id: `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      name: "New Executive",
      role: "Chief Officer",
      department: team?.name ?? "Executive",
      color: team?.color ?? "#EDE9DF",
      parentId: null,
    };
    setNodes((prev) => [...prev, node]);
    return node;
  }, [teams]);

  const removeNode = useCallback(
    (id) => {
      if (rootIds.has(id) && rootIds.size <= 1) return 0;
      const doomed = subtreeIds(nodes, id);
      setNodes((prev) => prev.filter((n) => !doomed.has(n.id)));
      return doomed.size;
    },
    [nodes, rootIds]
  );

  const reparent = useCallback(
    (id, newParentId) => {
      if (newParentId === id) return;
      if (newParentId && subtreeIds(nodes, id).has(newParentId)) return;
      setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, parentId: newParentId } : n)));
    },
    [nodes]
  );

  const resetAll = useCallback(() => {
    setNodes(SEED_NODES.map((n) => ({ ...n })));
    setTeams(DEFAULT_TEAMS.map((t) => ({ ...t })));
  }, []);

  const lineage = useCallback(
    (id) => ({
      ancestors: ancestorIds(nodes, id),
      descendants: subtreeIds(nodes, id),
    }),
    [nodes]
  );

  return {
    nodes,
    byId,
    rootIds,
    addRootNode,
    mode,
    setMode,
    cardSize,
    setCardSize,
    teams,
    addTeam,
    removeTeam,
    updateTeam,
    background,
    setBackground,
    depths,
    levels,
    departmentNames,
    addNode,
    updateNode,
    removeNode,
    reparent,
    resetAll,
    lineage,
  };
}
